import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import _ from "lodash";

import { URLS } from "config/urls";
import { AssessmentIcon, LockOutlinedIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { useCaching } from "utils/common";
import { BOX_MAX_WIDTH } from "utils/constants";
import { format, FORMATS, parseJSON } from "utils/datetime";
import { handleInvalidRequest } from "utils/sdk";

import Box from "components/Box";
import Button from "components/Button";
import PageHeader from "components/PageHeader";
import Paper from "components/Paper";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Text from "components/Text";
import toast from "components/Toast";
import Tooltip from "components/Tooltip";
import { useConfirmationDialog } from "components/useConfirmationDialog";
import { usePageTitle } from "components/usePageTitle";

import ActionButtons from "entities/TableStorage/components/ActionButtons";
import HeaderSubTitle from "entities/TableStorage/components/HeaderSubTitle";
import Table from "entities/TableStorage/components/Table";
import TableEditModeToggle from "entities/TableStorage/components/TableEditModeToggle";
import TableSkeleton from "entities/TableStorage/components/TableSkeleton";
import TableUpdateDialogButton from "entities/TableStorage/components/TableUpdateDialogButton";
import { useTableUpdateSucceeded } from "entities/TableStorage/hooks";
import {
  ITable,
  tableImportDelete,
  tableUpdate,
  TableUpdateData,
  useTableRetrieve,
} from "entities/TableStorage/sdk";
import { applyChangesToTableData } from "entities/TableStorage/utils";

const Details = () => {
  const params = useParams<{
    tableId: string;
  }>();
  const tableId = Number(params.tableId);

  const {
    data: table,
    mutate: mutateTable,
    error: tableError,
  } = useTableRetrieve({ tableId });

  const [tableData, setTableData] = useCaching<ITable["rows"]>(
    table?.rows || []
  );
  const [editMode, setEditMode] = useState(false);
  const [changedCells, setChangedCells] = useState<{
    [key: string]: string | null;
  }>({});
  const [resetTableData, setResetTableData] = useState(false);

  const { show: showConfirmationDialog } = useConfirmationDialog();

  useTableUpdateSucceeded({
    successEventHandler: () => {
      mutateTable();
    },
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (tableError?.status === 404) {
      navigate(URLS.NOT_FOUND, { replace: true });
    }
  }, [tableError, navigate]);

  usePageTitle(
    !_.isUndefined(table)
      ? `Builders Patch: ${table.name} - Table storage`
      : "Builders Patch: Table storage"
  );

  const handleTableUpdate = useCallback(
    ({ values }: { values: TableUpdateData }) =>
      tableUpdate({ tableId, data: values })
        .then(() => {
          toast.successMessage("Table updated successfully!");
          mutateTable();
        })
        .catch(handleInvalidRequest),
    [tableId, mutateTable]
  );

  const enableEditMode = useCallback(() => setEditMode(true), [setEditMode]);

  const disableEditMode = useCallback(() => setEditMode(false), [setEditMode]);

  const confirmDisableEditMode = useCallback(
    () =>
      showConfirmationDialog({
        onConfirm: () => {
          setTableData(table?.rows || []);
          disableEditMode();
          setChangedCells({});
          setResetTableData(true);
        },
        confirmButtonText: "Discard",
        message: "Discard changes?",
      }),
    [
      disableEditMode,
      showConfirmationDialog,
      table,
      setChangedCells,
      setTableData,
    ]
  );

  const handleSetEditMode = useCallback(() => {
    // When the user want to disable the edit mode but there are changes
    if (editMode && !_.isEmpty(changedCells)) {
      confirmDisableEditMode();
    } else if (!editMode) {
      enableEditMode();
    } else {
      disableEditMode();
    }
  }, [
    confirmDisableEditMode,
    disableEditMode,
    enableEditMode,
    changedCells,
    editMode,
  ]);

  const handleUpdateTableData = useCallback(
    ({ key, currentValue }) => {
      setTableData(tableData);
      setChangedCells((prev) => ({
        ...prev,
        [key]: currentValue,
      }));
      setResetTableData(false);
    },
    [setTableData, setChangedCells, tableData]
  );

  const handleSaveChanges = useCallback(() => {
    const tableDataWithChangedCells = applyChangesToTableData({
      tableData,
      changedCells,
    });

    const updatedTableData = tableDataWithChangedCells.map((row) => row.cells);

    handleTableUpdate({
      values: { rows: updatedTableData },
    }).then(() => {
      disableEditMode();
      setChangedCells({});
    });
  }, [
    tableData,
    changedCells,
    handleTableUpdate,
    disableEditMode,
    setChangedCells,
  ]);

  const onSaveChanges = useCallback(() => {
    showConfirmationDialog({
      onConfirm: handleSaveChanges,
      confirmButtonText: "Yes",
      message: "Are you sure you want to update the table data?",
    });
  }, [handleSaveChanges, showConfirmationDialog]);

  const handleDeleteImport = useCallback(() => {
    if (_.isNil(table?.deal_company_import_table?.id)) {
      return;
    }

    showConfirmationDialog({
      onConfirm: () =>
        tableImportDelete({
          tableImportId: table?.deal_company_import_table?.id as number,
        })
          .then(() => {
            toast.successMessage("Import table deleted successfully");
            mutateTable();
          })
          .catch(handleInvalidRequest),
      confirmButtonText: "Yes",
      message: "Are you sure you want to delete the range?",
    });
  }, [table, mutateTable, showConfirmationDialog]);

  const onCancelChanges = useCallback(() => {
    setChangedCells({});
    setResetTableData(true);
  }, []);

  const showButtons = useMemo(
    () => !_.isUndefined(table) && !table.is_locked,
    [table]
  );

  const showEditModeButton = useMemo(
    () => showButtons && !_.isEmpty(tableData),
    [showButtons, tableData]
  );

  const tableHasImport = useMemo(
    () => !_.isNil(table?.deal_company_import_table),
    [table]
  );

  return (
    <Paper
      sx={{
        overflowX: "auto",
        padding: theme.spacing(0, 4, 1, 4),
      }}
    >
      <Stack>
        <Stack
          spacing={4}
          sx={{ maxWidth: BOX_MAX_WIDTH, alignSelf: "center", width: "100%" }}
        >
          <PageHeader
            title={
              _.isUndefined(table) ? (
                <Skeleton height={55} width={250} />
              ) : (
                <Stack
                  direction="row"
                  spacing={theme.spacing(1)}
                  alignItems="center"
                >
                  <Text variant="h2" data-testid="page-header-name">
                    {table.name}
                  </Text>
                  {table.is_locked && (
                    <Tooltip title="This table is locked. The data is coming from the underwriting proforma upload.">
                      <LockOutlinedIcon
                        sx={{ width: "20px", height: "20px" }}
                      />
                    </Tooltip>
                  )}
                  {table.is_report && (
                    <Tooltip title="This table is an AI-powered generated report">
                      <AssessmentIcon />
                    </Tooltip>
                  )}
                </Stack>
              )
            }
            subTitle={<HeaderSubTitle table={table} />}
            backTitle="Company-level data tables"
            backLink={URLS.TABLE_STORAGE_LIST}
            actions={
              _.isUndefined(table) ? (
                <Skeleton height={60} width={50} />
              ) : (
                <ActionButtons
                  tableId={table.id}
                  initialValue={table.name}
                  updateCallback={mutateTable}
                  deleteCallback={() => navigate(URLS.TABLE_STORAGE_LIST)}
                  onImportDelete={
                    tableHasImport ? handleDeleteImport : undefined
                  }
                />
              )
            }
          />
          <Stack spacing={2}>
            <Box>
              {_.isUndefined(table) && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Skeleton height={63} width={150} />
                  <Skeleton height={63} width={150} />
                </Stack>
              )}
              {showButtons && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  {showEditModeButton && (
                    <TableEditModeToggle
                      checked={editMode}
                      onToggleChange={handleSetEditMode}
                    />
                  )}
                  <TableUpdateDialogButton
                    disabled={editMode}
                    onUpdate={handleTableUpdate}
                  />
                </Stack>
              )}
            </Box>
            {_.isUndefined(tableData) ? (
              <TableSkeleton />
            ) : (
              <Table
                tableData={tableData}
                editMode={editMode}
                updateTableData={handleUpdateTableData}
                resetTableData={resetTableData}
              />
            )}

            {!_.isUndefined(table) && (
              <Text
                variant="text2"
                sx={{
                  color: colors.gray80,
                  paddingBottom: 4,
                }}
              >
                Table created on{" "}
                {format(parseJSON(table.created_at), FORMATS.FRONTEND.DATE)} by{" "}
                {table.created_by.first_name} {table.created_by.last_name}
                {!_.isNil(table.updated_by) &&
                  ` • Last
          edited on ${format(parseJSON(table.updated_at), FORMATS.FRONTEND.DATE)} by ${table.updated_by.first_name} ${table.updated_by.last_name}`}
              </Text>
            )}

            {editMode && (
              <Stack direction="row" spacing={1} alignSelf="flex-end">
                <Button variant="outlined" onClick={onCancelChanges}>
                  Cancel changes
                </Button>
                <Button sx={{ alignSelf: "flex-end" }} onClick={onSaveChanges}>
                  Save changes
                </Button>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default Details;
